import { useCallback, useEffect, useState } from 'react'
import {
  FlaskConical,
  Play,
  Clock,
  DollarSign,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Star,
  History,
  Cpu,
} from 'lucide-react'
import {
  fetchAdminTestPrompts,
  fetchAdminTestModels,
  runAdminTestRubric,
  runAdminTestQuestions,
  runAdminTestGrading,
  updateAdminTestRating,
  fetchAdminTestHistory,
} from '../api/client'
import type {
  AdminTestType,
  AdminTestQualityRating,
  AdminTestRunResult,
  AdminTestRecord,
  AdminTestModelOption,
} from '../api/types'

type TabKey = 'rubric' | 'questions' | 'grading' | 'history'

const TEST_TYPE_LABELS: Record<AdminTestType, string> = {
  RUBRIC_EXTRACTION: 'Rubrik Cikarma',
  QUESTION_EXTRACTION: 'Soru Cikarma',
  GRADING: 'Notlandirma',
}

const QUALITY_OPTIONS: { value: AdminTestQualityRating; label: string; color: string; bg: string }[] = [
  { value: 'GREAT', label: 'Mukemmel', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  { value: 'GOOD_ENOUGH', label: 'Yeterli', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  { value: 'NEEDS_IMPROVEMENT', label: 'Gelistirilmeli', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  { value: 'FAILED', label: 'Basarisiz', color: 'var(--color-danger)', bg: 'var(--color-danger-bg)' },
]

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatCost(usd: number): string {
  if (usd === 0) return '$0'
  if (usd < 0.001) return `$${usd.toFixed(6)}`
  if (usd < 0.01) return `$${usd.toFixed(4)}`
  return `$${usd.toFixed(3)}`
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = new Date(iso)
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function QualityBadge({ rating }: { rating?: AdminTestQualityRating | null }) {
  if (!rating) return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Derecelendirilmedi</span>
  const opt = QUALITY_OPTIONS.find(o => o.value === rating)
  if (!opt) return null
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 'var(--radius-full)',
      fontSize: 12, fontWeight: 600, color: opt.color, background: opt.bg,
    }}>
      <Star size={12} />
      {opt.label}
    </span>
  )
}

// ── Test Runner Panel ────────────────────────────────────────────────────

function TestRunnerPanel({
  testType,
  defaultPrompt,
  models,
  multiFile,
}: {
  testType: 'rubric' | 'questions' | 'grading'
  defaultPrompt: string
  models: AdminTestModelOption[]
  multiFile?: boolean
}) {
  const [prompt, setPrompt] = useState(defaultPrompt)
  const [model, setModel] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AdminTestRunResult | null>(null)
  const [error, setError] = useState('')
  const [rating, setRating] = useState<AdminTestQualityRating | null>(null)
  const [notes, setNotes] = useState('')
  const [ratingStatus, setRatingStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [promptExpanded, setPromptExpanded] = useState(true)
  const [outputExpanded, setOutputExpanded] = useState(true)

  useEffect(() => {
    setPrompt(defaultPrompt)
  }, [defaultPrompt])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleRun = async () => {
    if (files.length === 0) {
      setError('Lutfen dosya yukleyin')
      return
    }
    setRunning(true)
    setResult(null)
    setError('')
    setRating(null)
    setNotes('')

    const formData = new FormData()
    formData.append('prompt', prompt)
    if (model) formData.append('model', model)

    if (testType === 'grading') {
      files.forEach(f => formData.append('files', f))
    } else {
      formData.append('file', files[0])
    }

    try {
      let res: AdminTestRunResult
      if (testType === 'rubric') {
        res = await runAdminTestRubric(formData)
      } else if (testType === 'questions') {
        res = await runAdminTestQuestions(formData)
      } else {
        res = await runAdminTestGrading(formData)
      }
      setResult(res)
      if (res.error) setError(res.error)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Test basarisiz oldu'
      setError(msg)
    } finally {
      setRunning(false)
    }
  }

  const handleSaveRating = async () => {
    if (!result || !rating) return
    setRatingStatus('saving')
    try {
      await updateAdminTestRating(result.id, rating, notes)
      setRatingStatus('saved')
    } catch {
      setRatingStatus('idle')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Model selection + file upload */}
      <div className="card card-padding">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Model</label>
              <select
                className="form-input"
                value={model}
                onChange={e => setModel(e.target.value)}
              >
                <option value="">Varsayilan model</option>
                {models.map(m => (
                  <option key={`${m.provider}/${m.model}`} value={m.model}>
                    {m.provider}: {m.model}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                {multiFile ? 'Dosyalar (bir veya daha fazla)' : 'Dosya'}
              </label>
              <input
                type="file"
                className="form-input"
                multiple={multiFile}
                accept="image/*,.pdf"
                onChange={handleFileChange}
                style={{ padding: '8px 12px' }}
              />
            </div>
          </div>

          {files.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {files.map((f, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--accent-copper-bg)', fontSize: 13,
                  color: 'var(--text-secondary)',
                }}>
                  <FileText size={14} />
                  {f.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Prompt editor */}
      <div className="card card-padding">
        <button
          onClick={() => setPromptExpanded(!promptExpanded)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 0, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
          }}
        >
          {promptExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          Prompt
        </button>
        {promptExpanded && (
          <div style={{ marginTop: 12 }}>
            <textarea
              className="form-input"
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={10}
              style={{
                fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5,
                resize: 'vertical', minHeight: 120,
                width: '100%', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setPrompt(defaultPrompt)}
                style={{ fontSize: 12 }}
              >
                Varsayilana Sifirla
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Run button */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          className="btn"
          onClick={handleRun}
          disabled={running || files.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: running ? 'var(--text-muted)' : 'var(--accent-primary)',
            color: 'var(--text-inverse)', padding: '10px 24px',
          }}
        >
          {running ? (
            <>
              <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
              Calisiyor...
            </>
          ) : (
            <>
              <Play size={16} />
              Testi Calistir
            </>
          )}
        </button>
        {running && (
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Bu islem birkac dakika surebilir...
          </span>
        )}
      </div>

      {/* Error */}
      {error && !result && (
        <div className="card card-padding" style={{ borderLeft: '3px solid var(--color-danger)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: 'var(--color-danger)' }}>
            <XCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Hata</div>
              <pre style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{error}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <>
          {/* Meta info */}
          <div className="card card-padding">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              {result.error ? (
                <AlertCircle size={18} style={{ color: 'var(--color-warning)' }} />
              ) : (
                <CheckCircle2 size={18} style={{ color: 'var(--color-success)' }} />
              )}
              <span style={{ fontWeight: 600 }}>
                {result.error ? 'Kismi Sonuc' : 'Basarili'}
              </span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                Test #{result.id}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              <div className="stat-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Cpu size={14} style={{ color: 'var(--accent-copper)' }} />
                  <span className="stat-card-label" style={{ fontSize: 11 }}>Model</span>
                </div>
                <div className="stat-card-value" style={{ fontSize: 13, marginTop: 4 }}>
                  {result.provider}/{result.model}
                </div>
              </div>
              <div className="stat-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Clock size={14} style={{ color: 'var(--accent-copper)' }} />
                  <span className="stat-card-label" style={{ fontSize: 11 }}>Sure</span>
                </div>
                <div className="stat-card-value" style={{ fontSize: 13, marginTop: 4 }}>
                  {formatDuration(result.duration_ms)}
                </div>
              </div>
              <div className="stat-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <DollarSign size={14} style={{ color: 'var(--accent-copper)' }} />
                  <span className="stat-card-label" style={{ fontSize: 11 }}>Maliyet</span>
                </div>
                <div className="stat-card-value" style={{ fontSize: 13, marginTop: 4 }}>
                  {formatCost(result.cost_usd)}
                </div>
              </div>
              <div className="stat-card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={14} style={{ color: 'var(--accent-copper)' }} />
                  <span className="stat-card-label" style={{ fontSize: 11 }}>Token</span>
                </div>
                <div className="stat-card-value" style={{ fontSize: 13, marginTop: 4 }}>
                  {result.input_tokens} / {result.output_tokens}
                </div>
              </div>
            </div>
          </div>

          {/* Files */}
          {result.file_ids.length > 0 && (
            <div className="card card-padding">
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Dosyalar</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {result.file_ids.map((fid, i) => (
                  <a
                    key={fid}
                    href={`/stream_file/${fid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-hover)', fontSize: 13,
                      color: 'var(--accent-copper)', textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={14} />
                    {result.file_names[i] || fid}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Raw output */}
          <div className="card card-padding">
            <button
              onClick={() => setOutputExpanded(!outputExpanded)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14,
              }}
            >
              {outputExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              Ham Cikti
            </button>
            {outputExpanded && (
              <pre style={{
                marginTop: 12, padding: 16, borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-sidebar)', fontSize: 13, lineHeight: 1.5,
                overflow: 'auto', maxHeight: 500, whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', border: '1px solid var(--border-light)',
              }}>
                {result.raw_output || result.error || 'Cikti yok'}
              </pre>
            )}
          </div>

          {/* Quality rating */}
          <div className="card card-padding">
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Kalite Degerlendirmesi</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {QUALITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setRating(opt.value)}
                  style={{
                    padding: '8px 16px', borderRadius: 'var(--radius-full)',
                    border: `2px solid ${rating === opt.value ? opt.color : 'var(--border-color)'}`,
                    background: rating === opt.value ? opt.bg : 'transparent',
                    color: rating === opt.value ? opt.color : 'var(--text-secondary)',
                    cursor: 'pointer', fontWeight: rating === opt.value ? 600 : 400,
                    fontSize: 13, transition: 'all var(--transition-fast)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="form-label">Notlar (istege bagli)</label>
              <textarea
                className="form-input"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Bu sonuc hakkinda notlar..."
                style={{ fontSize: 13 }}
              />
            </div>
            <button
              className="btn btn-sm"
              onClick={handleSaveRating}
              disabled={!rating || ratingStatus === 'saving'}
              style={{
                background: 'var(--accent-primary)',
                color: 'var(--text-inverse)',
                opacity: !rating ? 0.5 : 1,
              }}
            >
              {ratingStatus === 'saving' ? 'Kaydediliyor...' : ratingStatus === 'saved' ? 'Kaydedildi!' : 'Degerlendirmeyi Kaydet'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── History Panel ────────────────────────────────────────────────────────

function HistoryPanel() {
  const [records, setRecords] = useState<AdminTestRecord[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<string>('')
  const [filterQuality, setFilterQuality] = useState<string>('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const limit = 20

  const loadHistory = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { limit, offset: page * limit }
      if (filterType) params.test_type = filterType
      if (filterQuality) params.quality_rating = filterQuality
      const res = await fetchAdminTestHistory(params)
      setRecords(res.records)
      setTotal(res.total)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [filterType, filterQuality, page])

  useEffect(() => { loadHistory() }, [loadHistory])

  const totalPages = Math.ceil(total / limit)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filters */}
      <div className="card card-padding">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Test Tipi</label>
            <select className="form-input" value={filterType} onChange={e => { setFilterType(e.target.value); setPage(0) }}>
              <option value="">Tumu</option>
              <option value="RUBRIC_EXTRACTION">Rubrik Cikarma</option>
              <option value="QUESTION_EXTRACTION">Soru Cikarma</option>
              <option value="GRADING">Notlandirma</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Kalite</label>
            <select className="form-input" value={filterQuality} onChange={e => { setFilterQuality(e.target.value); setPage(0) }}>
              <option value="">Tumu</option>
              {QUALITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Yukleniyor...</div>
      ) : records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Kayit bulunamadi</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {records.map(rec => (
            <HistoryRow
              key={rec.id}
              record={rec}
              expanded={expandedId === rec.id}
              onToggle={() => setExpandedId(expandedId === rec.id ? null : rec.id)}
              onRatingChange={loadHistory}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, alignItems: 'center' }}>
          <button
            className="btn btn-sm btn-outline"
            disabled={page === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
          >
            Onceki
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {page + 1} / {totalPages}
          </span>
          <button
            className="btn btn-sm btn-outline"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Sonraki
          </button>
        </div>
      )}
    </div>
  )
}

function HistoryRow({ record, expanded, onToggle, onRatingChange }: {
  record: AdminTestRecord
  expanded: boolean
  onToggle: () => void
  onRatingChange: () => void
}) {
  const [rating, setRating] = useState<AdminTestQualityRating | null>(record.quality_rating || null)
  const [notes, setNotes] = useState(record.notes || '')
  const [ratingStatus, setRatingStatus] = useState<'idle' | 'saving' | 'saved'>('idle')

  const handleSaveRating = async () => {
    if (!rating) return
    setRatingStatus('saving')
    try {
      await updateAdminTestRating(record.id, rating, notes)
      setRatingStatus('saved')
      onRatingChange()
    } catch {
      setRatingStatus('idle')
    }
  }

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 20px', textAlign: 'left',
        }}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{
          padding: '2px 8px', borderRadius: 'var(--radius-full)',
          background: 'var(--accent-copper-bg)', color: 'var(--accent-copper)',
          fontSize: 11, fontWeight: 600,
        }}>
          {TEST_TYPE_LABELS[record.test_type] || record.test_type}
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {record.provider}/{record.model}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {formatDuration(record.duration_ms)} &middot; {formatCost(record.cost_usd)}
        </span>
        <QualityBadge rating={record.quality_rating} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {formatDate(record.created_at)}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 16, borderTop: '1px solid var(--border-light)' }}>
          {/* Meta */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 16 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Model</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{record.provider}/{record.model}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Sure</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{formatDuration(record.duration_ms)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Maliyet</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{formatCost(record.cost_usd)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Token (giris/cikis)</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{record.input_tokens} / {record.output_tokens}</div>
            </div>
          </div>

          {/* Files */}
          {record.file_ids.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Dosyalar</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {record.file_ids.map((fid, i) => (
                  <a
                    key={fid}
                    href={`/stream_file/${fid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-hover)', fontSize: 12,
                      color: 'var(--accent-copper)', textDecoration: 'none',
                    }}
                  >
                    <ExternalLink size={12} />
                    {record.file_names[i] || fid}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Prompt used */}
          {record.prompt_used && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Kullanilan Prompt</div>
              <pre style={{
                padding: 12, borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-sidebar)', fontSize: 12, lineHeight: 1.5,
                overflow: 'auto', maxHeight: 200, whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', border: '1px solid var(--border-light)',
                margin: 0,
              }}>
                {record.prompt_used}
              </pre>
            </div>
          )}

          {/* Raw output */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Ham Cikti</div>
            {record.error_message && (
              <div style={{ color: 'var(--color-danger)', fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <XCircle size={14} /> {record.error_message}
              </div>
            )}
            <pre style={{
              padding: 12, borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-sidebar)', fontSize: 12, lineHeight: 1.5,
              overflow: 'auto', maxHeight: 400, whiteSpace: 'pre-wrap',
              wordBreak: 'break-word', border: '1px solid var(--border-light)',
              margin: 0,
            }}>
              {record.raw_output || 'Cikti yok'}
            </pre>
          </div>

          {/* Rating */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>Kalite Degerlendirmesi</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {QUALITY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => { setRating(opt.value); setRatingStatus('idle') }}
                  style={{
                    padding: '5px 12px', borderRadius: 'var(--radius-full)',
                    border: `2px solid ${rating === opt.value ? opt.color : 'var(--border-color)'}`,
                    background: rating === opt.value ? opt.bg : 'transparent',
                    color: rating === opt.value ? opt.color : 'var(--text-secondary)',
                    cursor: 'pointer', fontWeight: rating === opt.value ? 600 : 400,
                    fontSize: 12, transition: 'all var(--transition-fast)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <textarea
                  className="form-input"
                  value={notes}
                  onChange={e => { setNotes(e.target.value); setRatingStatus('idle') }}
                  rows={1}
                  placeholder="Notlar..."
                  style={{ fontSize: 12 }}
                />
              </div>
              <button
                className="btn btn-sm"
                onClick={handleSaveRating}
                disabled={!rating || ratingStatus === 'saving'}
                style={{
                  background: 'var(--accent-primary)',
                  color: 'var(--text-inverse)',
                  opacity: !rating ? 0.5 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {ratingStatus === 'saved' ? 'Kaydedildi!' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Grading Test Panel (Mod A / Mod B aware) ─────────────────────────────

function GradingTestPanel({
  models,
  defaultPromptModB,
  defaultPromptModA,
  defaultPromptModC,
  defaultPromptModD,
}: {
  models: AdminTestModelOption[]
  defaultPromptModB: string
  defaultPromptModA: string
  defaultPromptModC: string
  defaultPromptModD: string
}) {
  type GradingMode = 'mod_b' | 'mod_a' | 'mod_c' | 'mod_d'
  const [mode, setMode] = useState<GradingMode>('mod_b')
  const [questionsJson, setQuestionsJson] = useState('')
  const [rubricText, setRubricText] = useState('')
  const [prompt, setPrompt] = useState('')
  const [promptExpanded, setPromptExpanded] = useState(true)
  const [model, setModel] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<AdminTestRunResult | null>(null)
  const [error, setError] = useState('')
  const [rating, setRating] = useState<AdminTestQualityRating | null>(null)
  const [notes, setNotes] = useState('')
  const [ratingStatus, setRatingStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [outputExpanded, setOutputExpanded] = useState(true)
  const [rubricPdf, setRubricPdf] = useState<File | null>(null)
  const [questionsPdf, setQuestionsPdf] = useState<File | null>(null)
  // Mod D: question extraction state
  const [modDQuestionsPdf, setModDQuestionsPdf] = useState<File | null>(null)
  const [modDRubricPdf, setModDRubricPdf] = useState<File | null>(null)
  const [modDExtractedJson, setModDExtractedJson] = useState('')
  const [modDExtracting, setModDExtracting] = useState(false)
  const [modDExtractError, setModDExtractError] = useState('')

  // Sync prompt when defaults load (async) or mode changes
  const defaultForMode = useCallback(
    (m: GradingMode) => m === 'mod_a' ? defaultPromptModA : m === 'mod_c' ? defaultPromptModC : m === 'mod_d' ? defaultPromptModD : defaultPromptModB,
    [defaultPromptModB, defaultPromptModA, defaultPromptModC, defaultPromptModD]
  )

  useEffect(() => {
    setPrompt(defaultForMode(mode))
  }, [defaultPromptModB, defaultPromptModA, defaultPromptModC, defaultPromptModD]) // only re-sync when defaults arrive, not on every mode change

  const handleModeChange = (m: GradingMode) => {
    setMode(m)
    setPrompt(defaultForMode(m))
    if (m !== 'mod_c') {
      setRubricPdf(null)
      setQuestionsPdf(null)
    }
    if (m !== 'mod_d') {
      setModDQuestionsPdf(null)
      setModDRubricPdf(null)
      setModDExtractedJson('')
      setModDExtractError('')
    }
  }

  const handleExtractModD = async () => {
    if (!modDQuestionsPdf) { setModDExtractError('Lütfen sorular PDF yükleyin'); return }
    setModDExtracting(true); setModDExtractedJson(''); setModDExtractError('')
    const formData = new FormData()
    formData.append('file', modDQuestionsPdf)
    if (model) formData.append('model', model)
    try {
      const res = await runAdminTestQuestions(formData)
      if (res.error) { setModDExtractError(res.error); return }
      setModDExtractedJson(res.raw_output || '')
    } catch (err: unknown) {
      setModDExtractError(err instanceof Error ? err.message : 'Extraction başarısız')
    } finally { setModDExtracting(false) }
  }

  const handleRun = async () => {
    if (files.length === 0) { setError('Lutfen dosya yukleyin'); return }
    if (mode === 'mod_a' && !questionsJson.trim()) { setError('Mod A için sorular JSON gerekli'); return }
    if (mode === 'mod_d' && !modDExtractedJson.trim()) { setError('Mod D için önce soruları extract edin'); return }
    setRunning(true); setResult(null); setError(''); setRating(null); setNotes('')
    const formData = new FormData()
    files.forEach(f => formData.append('files', f))
    if (model) formData.append('model', model)
    formData.append('grading_mode', mode)
    if (rubricText.trim()) formData.append('rubric_text', rubricText)
    if (questionsJson.trim()) formData.append('questions_json', questionsJson)
    if (prompt.trim()) formData.append('prompt', prompt)
    if (mode === 'mod_c') {
      if (rubricPdf) formData.append('rubric_file', rubricPdf)
      if (questionsPdf) formData.append('questions_file', questionsPdf)
    }
    if (mode === 'mod_d') {
      if (modDRubricPdf) formData.append('rubric_file', modDRubricPdf)
      if (modDExtractedJson.trim()) formData.append('extracted_questions_json', modDExtractedJson)
    }
    try {
      const res = await runAdminTestGrading(formData)
      setResult(res)
      if (res.error) setError(res.error)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Test basarisiz oldu')
    } finally { setRunning(false) }
  }

  const handleSaveRating = async () => {
    if (!result || !rating) return
    setRatingStatus('saving')
    try { await updateAdminTestRating(result.id, rating, notes); setRatingStatus('saved') }
    catch { setRatingStatus('idle') }
  }

  const textareaStyle: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12, width: '100%', boxSizing: 'border-box' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Model + file */}
      <div className="card card-padding">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="form-group">
            <label className="form-label">Model</label>
            <select className="form-input" value={model} onChange={e => setModel(e.target.value)}>
              <option value="">Varsayilan model</option>
              {models.map(m => (
                <option key={`${m.provider}/${m.model}`} value={m.model}>{m.provider}: {m.model}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Sınav kağıtları</label>
            <input type="file" accept="image/*,application/pdf" multiple
              onChange={e => setFiles(e.target.files ? Array.from(e.target.files) : [])}
              style={{ marginTop: 4 }} />
            {files.length > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{files.length} dosya seçildi</span>}
          </div>
        </div>
      </div>

      {/* Mode toggle + inputs */}
      <div className="card card-padding">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['mod_b', 'mod_a', 'mod_c', 'mod_d'] as GradingMode[]).map(m => (
            <button key={m} onClick={() => handleModeChange(m)} style={{
              padding: '8px 20px', borderRadius: 6, fontWeight: 600, fontSize: 13,
              cursor: 'pointer', border: '2px solid',
              borderColor: mode === m ? 'var(--accent-copper)' : 'var(--border-light)',
              background: mode === m ? 'var(--accent-copper)' : 'transparent',
              color: mode === m ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.15s',
            }}>
              {m === 'mod_b' ? 'Mod B — Sorular Yok' : m === 'mod_a' ? 'Mod A — Sorularla' : m === 'mod_c' ? 'Mod C — Responses API' : 'Mod D — Extract + Inject'}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
          {mode === 'mod_b'
            ? 'AI kağıttaki soruları kendisi tespit eder. Sorular opsiyonel — verilirse bağlam olarak kullanılır, skeleton oluşturulmaz.'
            : mode === 'mod_a'
            ? 'Sorular skeleton\'a dönüştürülür, AI bu yapıyı doldurur (schema-driven).'
            : mode === 'mod_c'
            ? 'OpenAI Responses API: Rubrik ve sorular PDF olarak Files API\'ye yüklenir, öğrenci kağıtları ayrı input_image olarak gönderilir. ChatGPT.com davranışını taklit eder.'
            : 'Sorular PDF\'den extract edilir, prompt içine JSON olarak enjekte edilir. Responses API kullanılır — sorular PDF dosyası gönderilmez.'}
        </div>

        {/* Mod C: auxiliary PDF uploads */}
        {mode === 'mod_c' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Rubrik PDF <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opsiyonel)</span></label>
              <input
                type="file"
                accept="application/pdf"
                onChange={e => setRubricPdf(e.target.files?.[0] ?? null)}
                style={{ marginTop: 4 }}
              />
              {rubricPdf && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rubricPdf.name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Sorular PDF <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opsiyonel)</span></label>
              <input
                type="file"
                accept="application/pdf"
                onChange={e => setQuestionsPdf(e.target.files?.[0] ?? null)}
                style={{ marginTop: 4 }}
              />
              {questionsPdf && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{questionsPdf.name}</span>}
            </div>
          </div>
        )}

        {/* Mod D: extract questions then inject */}
        {mode === 'mod_d' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Sorular PDF <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => setModDQuestionsPdf(e.target.files?.[0] ?? null)}
                  style={{ marginTop: 4 }}
                />
                {modDQuestionsPdf && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{modDQuestionsPdf.name}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Rubrik PDF <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(opsiyonel)</span></label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={e => setModDRubricPdf(e.target.files?.[0] ?? null)}
                  style={{ marginTop: 4 }}
                />
                {modDRubricPdf && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{modDRubricPdf.name}</span>}
              </div>
            </div>
            <div>
              <button
                onClick={handleExtractModD}
                disabled={modDExtracting || !modDQuestionsPdf}
                style={{
                  padding: '8px 20px', borderRadius: 6, fontWeight: 600, fontSize: 13,
                  cursor: modDExtracting || !modDQuestionsPdf ? 'not-allowed' : 'pointer',
                  border: '2px solid var(--accent-copper)',
                  background: modDExtracting || !modDQuestionsPdf ? 'var(--border-light)' : 'var(--accent-copper)',
                  color: modDExtracting || !modDQuestionsPdf ? 'var(--text-muted)' : '#fff',
                }}
              >
                {modDExtracting ? 'Extract ediliyor...' : 'Soruları Çıkar'}
              </button>
              {modDExtractError && <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 6 }}>{modDExtractError}</div>}
            </div>
            {modDExtractedJson && (
              <div className="form-group">
                <label className="form-label">Extract Edilen Sorular JSON <span style={{ color: 'var(--color-success)', fontWeight: 400 }}>✓ — düzenleyebilirsiniz</span></label>
                <textarea
                  className="form-input"
                  rows={10}
                  value={modDExtractedJson}
                  onChange={e => setModDExtractedJson(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 12, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Questions JSON — required in Mod A, optional in Mod B, hidden in Mod C/D */}
        {mode !== 'mod_c' && mode !== 'mod_d' && (
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">
              Sorular JSON
              {mode === 'mod_a' && <span style={{ color: 'var(--color-danger)' }}> *</span>}
              {mode === 'mod_b' && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (opsiyonel — bağlam için)</span>}
            </label>
            <textarea
              className="form-input"
              rows={7}
              placeholder={'[\n  {\n    "question_number": "1",\n    "question_text": "...",\n    "maximum_points": 10,\n    "sub_questions": []\n  }\n]'}
              value={questionsJson}
              onChange={e => setQuestionsJson(e.target.value)}
              style={textareaStyle}
            />
          </div>
        )}

        {/* Rubric (Mod A and Mod B only) */}
        {mode !== 'mod_c' && mode !== 'mod_d' && (
          <div className="form-group">
            <label className="form-label">Rubrik / Değerlendirme Rehberi
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (opsiyonel)</span>
            </label>
            <textarea
              className="form-input"
              rows={4}
              placeholder="Boş bırakın — AI akademik yargı kullanır."
              value={rubricText}
              onChange={e => setRubricText(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        )}
      </div>

      {/* Prompt */}
      <div className="card card-padding">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: promptExpanded ? 12 : 0 }}>
          <button onClick={() => setPromptExpanded(!promptExpanded)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, flex: 1, minWidth: 0 }}>
            {promptExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span style={{ fontWeight: 600, fontSize: 14 }}>Prompt</span>
            {mode === 'mod_a' && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                — <code style={{ fontSize: 11 }}>{'{questions_text}'}</code> yerine skeleton enjekte edilir
              </span>
            )}
          </button>
          <button onClick={() => setPrompt(defaultForMode(mode))} className="btn btn-outline btn-sm"
            style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
            Varsayılana Sıfırla
          </button>
        </div>
        {promptExpanded && (
          <textarea
            className="form-input"
            rows={18}
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            style={{ ...textareaStyle, resize: 'vertical' }}
          />
        )}
      </div>

      {/* Run button */}
      <button className="btn btn-primary" onClick={handleRun} disabled={running}
        style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}>
        {running ? <><span className="spinner" />Çalışıyor...</> : <><Play size={16} />Testi Çalıştır</>}
      </button>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Result */}
      {result && (
        <div className="card card-padding">
          <button onClick={() => setOutputExpanded(!outputExpanded)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', width: '100%', padding: 0, marginBottom: outputExpanded ? 12 : 0 }}>
            {outputExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span style={{ fontWeight: 600 }}>Sonuç</span>
          </button>
          {outputExpanded && (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                {[
                  { label: 'Süre', value: formatDuration(result.duration_ms) },
                  { label: 'Maliyet', value: formatCost(result.cost_usd) },
                  { label: 'Girdi token', value: result.input_tokens.toLocaleString() },
                  { label: 'Çıktı token', value: result.output_tokens.toLocaleString() },
                  { label: 'Model', value: result.provider ? `${result.provider}/${result.model}` : result.model || '—' },
                ].map(s => (
                  <div key={s.label} className="stat-card" style={{ minWidth: 100 }}>
                    <div className="stat-card-label" style={{ fontSize: 11 }}>{s.label}</div>
                    <div className="stat-card-value" style={{ fontSize: 14 }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <pre style={{ background: 'var(--bg-secondary)', padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto', maxHeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {result.raw_output || JSON.stringify(result, null, 2)}
              </pre>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {QUALITY_OPTIONS.map(o => (
                    <button key={o.value} onClick={() => setRating(o.value)}
                      style={{ padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        border: `2px solid ${o.color}`, background: rating === o.value ? o.bg : 'transparent', color: o.color }}>
                      {o.label}
                    </button>
                  ))}
                </div>
                <textarea className="form-input" rows={2} placeholder="Not (opsiyonel)" value={notes} onChange={e => setNotes(e.target.value)} />
                <button className="btn btn-sm btn-primary" onClick={handleSaveRating} disabled={!rating || ratingStatus === 'saving'}
                  style={{ alignSelf: 'flex-start' }}>
                  {ratingStatus === 'saved' ? '✓ Kaydedildi' : ratingStatus === 'saving' ? 'Kaydediliyor...' : 'Derecelendir'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────

export default function AdminTestPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('rubric')
  const [prompts, setPrompts] = useState<{ rubric: string; questions: string; grading: string; gradingModA: string; gradingModC: string; gradingModD: string }>({
    rubric: '', questions: '', grading: '', gradingModA: '', gradingModC: '', gradingModD: '',
  })
  const [models, setModels] = useState<AdminTestModelOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetchAdminTestPrompts().catch(() => ({ rubric_extraction: '', question_extraction: '', grading: '' })),
      fetchAdminTestModels().catch(() => ({ models: [] })),
    ]).then(([p, m]) => {
      setPrompts({
        rubric: p.rubric_extraction,
        questions: p.question_extraction,
        grading: p.grading,
        gradingModA: p.grading_mod_a || '',
        gradingModC: p.grading_mod_c || '',
        gradingModD: p.grading_mod_d || '',
      })
      setModels(m.models)
      setLoading(false)
    })
  }, [])

  const tabs: { key: TabKey; label: string; icon: typeof FlaskConical }[] = [
    { key: 'rubric', label: 'Rubrik Cikarma', icon: FileText },
    { key: 'questions', label: 'Soru Cikarma', icon: FlaskConical },
    { key: 'grading', label: 'Notlandirma', icon: Play },
    { key: 'history', label: 'Gecmis', icon: History },
  ]

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">GenAI Test Laboratuvari</h2>
        <p className="page-header-sub">
          Rubrik cikarma, soru cikarma ve notlandirma modellerini test edin. Farkli promptlar ve modeller deneyin.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 24,
        borderBottom: '1px solid var(--border-light)', paddingBottom: 0,
      }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 14, fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? 'var(--accent-copper)' : 'var(--text-muted)',
              borderBottom: activeTab === tab.key ? '2px solid var(--accent-copper)' : '2px solid transparent',
              marginBottom: -1, transition: 'all var(--transition-fast)',
            }}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          Yukleniyor...
        </div>
      ) : (
        <div style={{ maxWidth: 900 }}>
          {activeTab === 'rubric' && (
            <TestRunnerPanel
              testType="rubric"
              defaultPrompt={prompts.rubric}
              models={models}
            />
          )}
          {activeTab === 'questions' && (
            <TestRunnerPanel
              testType="questions"
              defaultPrompt={prompts.questions}
              models={models}
            />
          )}
          {activeTab === 'grading' && (
            <GradingTestPanel
              models={models}
              defaultPromptModB={prompts.grading}
              defaultPromptModA={prompts.gradingModA}
              defaultPromptModC={prompts.gradingModC}
              defaultPromptModD={prompts.gradingModD}
            />
          )}
          {activeTab === 'history' && (
            <HistoryPanel />
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
